/**
 * App-level auth utilities.
 * Token storage / JWT helpers live in @repo/api — re-export the ones
 * that app code needs so imports stay clean.
 */
import { redirect } from "@tanstack/react-router";
import { getStoredTokens, isTokenExpired } from "@repo/api";

export {
  isTokenExpired,
  decodeJwtPayload,
  getStoredTokens,
  storeTokens,
  clearTokens,
  TOKEN_KEYS,
} from "@repo/api";

/**
 * Synchronous session check — mirrors AuthProvider's bootstrap logic
 * (see AuthContext.tsx): a non-expired access token, or an expired one
 * paired with a refresh token (fetchClient refreshes on the next call).
 * Safe to call outside React (e.g. in a route `beforeLoad`).
 */
export function hasValidSession(): boolean {
  const { accessToken, refreshToken } = getStoredTokens();
  if (!accessToken) return false;
  if (isTokenExpired(accessToken)) return Boolean(refreshToken);
  return true;
}

/**
 * Route `beforeLoad` guard for protected routes: redirect to `/login`
 * when there is no valid session. Prevents protected route components
 * (and their MFEs) from mounting for logged-out users — including the
 * transient window where the root Outlet renders before AuthContext's
 * redirect effect fires.
 */
export function requireAuth(): void {
  if (!hasValidSession()) {
    throw redirect({ to: "/login" });
  }
}
